import { useCallback, useEffect, useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  createExternalApiKey,
  listExternalApiKeys,
  revokeExternalApiKey,
} from "@/services/externalApiKeysService";
import { Banner, SectionHeading } from "./settingsUi";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function ApiKeysSettingsSection() {
  const { activeWorkspaceId } = useWorkspace();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const loadKeys = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setBanner(null);
    try {
      const data = await listExternalApiKeys(activeWorkspaceId);
      setKeys(data.items || []);
    } catch (err) {
      setBanner({ type: "err", text: err instanceof Error ? err.message : "Could not load API keys." });
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!activeWorkspaceId || !keyName.trim()) return;
    setCreating(true);
    setBanner(null);
    try {
      const created = await createExternalApiKey(activeWorkspaceId, keyName.trim());
      setRevealedKey(created.api_key);
      setCreateOpen(false);
      setKeyName("");
      await loadKeys();
      setBanner({ type: "ok", text: "API key created. Copy it now — it will not be shown again." });
    } catch (err) {
      setBanner({ type: "err", text: err instanceof Error ? err.message : "Could not create API key." });
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (keyId) => {
    if (!activeWorkspaceId || !window.confirm("Revoke this API key? External apps using it will stop working immediately.")) {
      return;
    }
    setRevokingId(keyId);
    setBanner(null);
    try {
      await revokeExternalApiKey(activeWorkspaceId, keyId);
      await loadKeys();
      setBanner({ type: "ok", text: "API key revoked." });
    } catch (err) {
      setBanner({ type: "err", text: err instanceof Error ? err.message : "Could not revoke API key." });
    } finally {
      setRevokingId(null);
    }
  };

  const copyRevealed = async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setBanner({ type: "ok", text: "API key copied to clipboard." });
    } catch {
      setBanner({ type: "err", text: "Could not copy to clipboard." });
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <SectionHeading
        label="API Keys"
        desc="Create workspace-scoped keys for external integrations. Keys authenticate via the external API gateway."
      />
      {banner && <Banner type={banner.type} text={banner.text} onDismiss={() => setBanner(null)} />}

      {revealedKey && (
        <div className="ui-card-static space-y-3 !border-amber-200 !bg-amber-50/80">
          <p className="text-sm font-semibold text-amber-900">Copy your new API key</p>
          <code className="block break-all rounded-lg bg-white px-3 py-2 text-xs text-slate-800 ring-1 ring-amber-200">
            {revealedKey}
          </code>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="ui-btn-primary" onClick={copyRevealed}>
              Copy key
            </button>
            <button type="button" className="ui-btn-secondary" onClick={() => setRevealedKey(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="ui-card-static space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            Active keys for this workspace. Use{" "}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">Authorization: ApiKey &lt;key&gt;</code> on{" "}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">/api/v1/external/&#123;service&#125;/…</code>
          </p>
          <button type="button" className="ui-btn-primary" onClick={() => setCreateOpen(true)}>
            Create API key
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading keys…</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-slate-500">No API keys yet.</p>
        ) : (
          <ul className="divide-y divide-slate-200/80">
            {keys.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{k.name}</p>
                  <p className="text-xs text-slate-500">
                    {k.key_prefix}_•••••• · Created {formatDate(k.created_at)}
                    {k.last_used_at ? ` · Last used ${formatDate(k.last_used_at)}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50"
                  disabled={revokingId === k.id}
                  onClick={() => onRevoke(k.id)}
                >
                  {revokingId === k.id ? "Revoking…" : "Revoke"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="ui-card-static space-y-2 text-sm text-slate-600">
        <p className="font-semibold text-slate-800">Example</p>
        <pre className="overflow-x-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
{`curl -H "Authorization: ApiKey YOUR_KEY" \\
  https://gateway.example.com/api/v1/external/reporting/dashboard/summary`}
        </pre>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <form
            className="ui-card-static w-full max-w-md space-y-4"
            onSubmit={onCreate}
          >
            <h3 className="text-lg font-semibold text-slate-900">Create API key</h3>
            <label className="block text-sm font-medium text-slate-700">
              Name
              <input
                className="ui-input mt-1"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g. CRM integration"
                required
                maxLength={255}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="ui-btn-secondary" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="ui-btn-primary" disabled={creating || !keyName.trim()}>
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
